import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Business } from '@models/businesses';
import { BusinessState } from '@state/business';
import LogRocket from 'logrocket';
import { StripeService } from '../../../shared/functions/stripe.service';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { ICON } from '@core/util/icon';
import {
	distinctUntilChanged,
	filter,
	map,
	shareReplay,
	switchMap,
	take,
	tap,
} from 'rxjs/operators';
import { UntypedFormControl, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { Select } from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GetRecentInvoicesSummaryResponse } from '@fnModels/stripe';
import Stripe from 'stripe';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { isSet } from '@core/util/value';
// import { stripeTestData } from 'src/app/shared/modules/stripe/data/stripeTestData';

@UntilDestroy()
@Component({
	selector: 'app-billing',
	templateUrl: './billing.component.html',
	styleUrls: ['./billing.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class BillingComponent implements OnInit {
	@Select(BusinessState.hasStripeCustomerId)
	hasStripeCustomerId$!: Observable<boolean>;
	@Select(BusinessState.getStripeBillingEmail)
	billingEmail$!: Observable<string>;

	invoiceLimit = 15;
	emailControl!: UntypedFormControl;
	loading$!: BehaviorSubject<boolean>;
	processing$!: Observable<boolean>;
	fetchingInvoices$!: BehaviorSubject<boolean>;
	saving$!: BehaviorSubject<boolean>;
	invoices$!: Observable<GetRecentInvoicesSummaryResponse<Stripe.Invoice>>; // @@todo
	disableStripeButton$: Observable<boolean>;
	balance$!: BehaviorSubject<number | null>;

	public ICON = ICON;
	public creatingStripeCustomer = false;
	public errorCreatingStripeCustomer;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public stripeCustomer: any;

	@ViewSelectSnapshot(BusinessState.getBusiness) business: Business;

	constructor(
		private stripeService: StripeService,
		private snackbarService: SnackbarService,
	) {}

	async ngOnInit(): Promise<void> {
		this.emailControl = new UntypedFormControl('', [
			Validators.email,
			Validators.required,
		]);

		this.loading$ = new BehaviorSubject(false);
		this.saving$ = new BehaviorSubject(false);
		this.balance$ = new BehaviorSubject(null);
		this.fetchingInvoices$ = new BehaviorSubject(false);
		this.processing$ = combineLatest([this.saving$, this.loading$]).pipe(
			map(([isSaving, isLoading]) => isSaving || isLoading),
		);

		this.disableStripeButton$ = combineLatest([
			this.processing$,
			this.billingEmail$,
		]).pipe(
			map(([isProcessing, isBillingEmail]) => isProcessing || !isBillingEmail),
		);

		// when the email being visible changes to true, pull the
		// latest value from the bilingEmail and display back for
		// updating to the user assuming that there actually is an email
		this.hasStripeCustomerId$
			.pipe(
				distinctUntilChanged(),
				filter((show) => !!show),
				switchMap(() => this.billingEmail$),
				filter((email) => !!email),
				untilDestroyed(this),
			)
			.subscribe((email) => this.emailControl.setValue(email));

		// pull recent invoices from stripe where applicable
		this.invoices$ = this.hasStripeCustomerId$.pipe(
			tap(() => this.fetchingInvoices$.next(true)),
			switchMap((hasId) =>
				hasId
					? this.stripeService.getRecentInvoices({
							limit: this.invoiceLimit,
						})
					: of<GetRecentInvoicesSummaryResponse<Stripe.Invoice>>({
							rows: [],
							hasMore: false,
						}),
			),
			shareReplay(1),
			tap(() => this.fetchingInvoices$.next(false)),
		);

		try {
			this.stripeCustomer = await this.stripeService
				.getBusinessBalance()
				.pipe(take(1))
				.toPromise();

			if (isSet(this.stripeCustomer?.balance)) {
				const balance =
					this.stripeCustomer.balance === 0
						? this.stripeCustomer?.balance
						: -1 * this.stripeCustomer?.balance;
				this.balance$.next(balance);
			}
		} catch (err) {
			console.log('Error', err);
			LogRocket.error('Issue loading customer account', err);
		}
	}

	async getBillingPortalRedirectURL(): Promise<void> {
		this.loading$.next(true);

		if (!this.business.stripeCustomerId) {
			await this.signuserUp();
		}

		if (!this.errorCreatingStripeCustomer) {
			try {
				const returnUrl = `${window.location.origin}/app/profile/billing`;

				const stripeUrl = await this.stripeService
					.getBillingPortalRedirectURL(returnUrl)
					.pipe(take(1))
					.toPromise();
				if (stripeUrl) {
					window.location = stripeUrl;
				}
			} catch (err) {
				LogRocket.error('error getting stripe redirect session url', err);
			} finally {
				this.loading$.next(false);
			}
		}
	}

	saveEmail() {
		const email = this.emailControl.value?.trim();

		this.saving$.next(true);

		// dispatch an action to update the email address

		this.stripeService.updateBillingEmailAddress(email).subscribe(
			(data) => {
				if (!data) {
					this.snackbarService.openSnackBar({
						text: 'Unable to save billing email.',
						type: 'error',
					});
				}
			},
			(err) => {
				this.snackbarService.openSnackBar({
					text: 'Unable to save billing email.',
					type: 'error',
				});

				LogRocket.error('error saving email in stripe', err);
			},
			() => this.saving$.next(false),
		);
	}

	async signuserUp() {
		try {
			if (!this.business.stripeCustomerId) {
				this.errorCreatingStripeCustomer = null;
				this.creatingStripeCustomer = true;

				await this.stripeService
					.createNewStripeCustomer(this.business.id)
					.pipe(take(1))
					.toPromise();

				this.creatingStripeCustomer = false;
			}
		} catch (err) {
			this.loading$.next(false);
			this.creatingStripeCustomer = false;
			this.errorCreatingStripeCustomer = err;
			LogRocket.error('error getting stripe redirect session url', err);
		}
	}
}
